import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { cx } from '../../lib/cx';

export interface DropdownOption<V extends string> {
  value: V;
  label: string;
}

/**
 * Випадний список у стилі CRM замість системного <select> (на macOS той відкривав сіре системне вікно).
 * Клавіатура: Enter/Пробіл/↓ відкривають, ↑↓ ходять, Enter вибирає, Esc закриває; клік поза списком — закрити.
 */
export function Dropdown<V extends string>({
  value,
  options,
  onChange,
  label,
  disabled,
  className,
}: {
  value: V;
  options: DropdownOption<V>[];
  onChange: (v: V) => void;
  label: string;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    setActive(Math.max(0, options.findIndex((o) => o.value === value)));
    const away = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (v: V) => {
    setOpen(false);
    if (v !== value) onChange(v);
  };
  const onKey = (e: KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const o = options[active];
      if (o) pick(o.value);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div ref={root} className={cx('relative shrink-0', className)}>
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        disabled={disabled}
        onClick={() => setOpen((x) => !x)}
        onKeyDown={onKey}
        className="h-10 w-full max-w-[260px] pl-3 pr-9 rounded border border-line bg-bg text-left font-semibold text-sm truncate relative hover:border-line-strong disabled:opacity-50"
      >
        {current?.label ?? label}
        <svg
          className={cx('absolute right-3 top-1/2 -translate-y-1/2 transition-transform', open && 'rotate-180')}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute z-50 left-0 top-full mt-1.5 min-w-full w-max max-w-[320px] max-h-[70vh] overflow-y-auto m-0 p-1.5 list-none bg-bg border border-line rounded-xl shadow-2xl"
        >
          {options.map((o, i) => {
            const selected = o.value === value;
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(o.value)}
                className={cx(
                  'flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm cursor-pointer select-none',
                  i === active && 'bg-hov',
                  selected && 'font-semibold',
                )}
              >
                <span>{o.label}</span>
                {selected && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
